/**
 * OutreachRowPills — the Outreach cell (owner sign-off 2026-10-15): one state pill, at most one red badge when the admin must
 * act, and one muted "next step / why not" line. rowLabels decides every word; this only draws them. Hover text sits in a
 * focusable tooltip; the Stopped pill also carries its reason as screen-reader text, because a tap never opens a tooltip.
 */
import { memo, type ReactNode } from 'react';
import type { OutreachRow } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { PILL_BASE } from './OutreachStatusPill';
import { ROSE_PILL } from './brandPill';
import { CONTACTED_PILL_LABEL, type PillTone, rowLabels, waitingSendHint } from './rowLabels';
import { FOCUS_RING, StatusTooltip } from './StatusTooltip';

export { waitingSendHint };

const PILL_TONE: Record<PillTone, string> = {
  neutral: 'bg-[var(--stone-200)] text-(--foreground-secondary-default)',
  brand: 'bg-[var(--brand-100)] text-[var(--brand-600)]',
  replied: 'bg-amber-100 text-[var(--foreground-warning-default)]',
  good: 'bg-emerald-100 text-emerald-700',
  stopped: 'bg-stone-200 text-stone-600',
  removed: 'bg-rose-100 text-rose-700',
  stuck: ROSE_PILL,
};

function Hovered({ hint, testId, className, children }: { hint?: string; testId: string; className: string; children: ReactNode }) {
  if (!hint) return <span data-testid={testId} className={className}>{children}</span>;
  return (
    <StatusTooltip content={hint}>
      <span data-testid={testId} tabIndex={0} className={cn(className, FOCUS_RING)}>{children}</span>
    </StatusTooltip>
  );
}

function OutreachRowPillsView({
  row,
  campaignZone,
  autoFollowups = true,
  hideContactedPill = false,
  nameStopReason = false,
}: {
  row: OutreachRow;
  campaignZone?: string;
  /** The campaign's Automatic follow-ups switch; OFF hides the automatic-step countdown. */
  autoFollowups?: boolean;
  /** The Contacted tab already says "Contacted", so its rows drop the word; the badge and the line stay. */
  hideContactedPill?: boolean;
  /** The thread header names why a row stopped ("Stopped · Ghosted"); the inbox keeps the bare word with the reason on hover. */
  nameStopReason?: boolean;
}) {
  const { pill, badge, doNotContact, line } = rowLabels(row, { campaignZone, autoFollowups });
  const named = nameStopReason ? pill.stopReason : undefined;
  const showPill = !(hideContactedPill && pill.label === CONTACTED_PILL_LABEL);
  return (
    <div className="flex flex-col items-end gap-1">
      {(showPill || badge || doNotContact) && (
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {showPill && (
            <Hovered hint={pill.hint} testId="row-pill" className={cn(PILL_BASE, PILL_TONE[pill.tone])}>
              {named ? `${pill.label} · ${named}` : pill.label}
              {pill.hint && !named && <span className="sr-only">{` · ${pill.hint}`}</span>}
            </Hovered>
          )}
          {badge && <Hovered hint={badge.hint} testId="row-badge" className={cn(PILL_BASE, ROSE_PILL)}>{badge.label}</Hovered>}
          {doNotContact && <Hovered hint={doNotContact.hint} testId="row-dnc" className={cn(PILL_BASE, ROSE_PILL)}>{doNotContact.label}</Hovered>}
        </div>
      )}
      {line && <Hovered hint={line.hint} testId="row-line" className="text-right text-xs text-muted-foreground">{line.text}</Hovered>}
    </div>
  );
}

/** Memoised: rowLabels walks the whole row, and a table re-renders every cell on each interaction. */
export const OutreachRowPills = memo(OutreachRowPillsView);
